import { useState } from 'react';
import AppLayout from '@/components/feature/AppLayout';
import TopBar from '@/components/feature/TopBar';
import ActionBar from '@/components/feature/ActionBar';
import { useStore } from '@/hooks/useStore';
import ModalCategorias from './components/ModalCategorias';
import type { Produto } from '@/mocks/store';

type ModoPreco = 'margem' | 'lucro_fixo' | 'manual';

function calcPrecoByMargem(custo: number, custoExtra: number, margem: number) {
  const total = custo + custoExtra;
  if (margem >= 100 || total <= 0) return 0;
  return total / (1 - margem / 100);
}

function calcPrecoByLucroFixo(custo: number, custoExtra: number, lucroFixo: number) {
  return custo + custoExtra + lucroFixo;
}

function calcMargem(custo: number, custoExtra: number, preco: number) {
  const total = custo + custoExtra;
  if (preco <= 0 || total <= 0) return 0;
  return ((preco - total) / preco) * 100;
}

function fmt(v: number) {
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

interface FormState {
  nome: string;
  categoria: string;
  custo: string;
  custoExtra: string;
  estoque: string;
  status: 'ativo' | 'inativo';
  modoPreco: ModoPreco;
  margemDesejada: string;
  lucroFixo: string;
  precoManual: string;
}

const emptyForm: FormState = {
  nome: '', categoria: '', custo: '', custoExtra: '0',
  estoque: '', status: 'ativo',
  modoPreco: 'margem', margemDesejada: '50', lucroFixo: '', precoManual: '',
};

// Cores para o ranking de categorias
const RANK_COLORS = [
  'text-yellow-400 bg-yellow-400/10 border-yellow-400/20',
  'text-slate-300 bg-slate-300/10 border-slate-300/20',
  'text-amber-600 bg-amber-600/10 border-amber-600/20',
];

export default function ProdutosPage() {
  const {
    produtos, vendas, categorias,
    addProduto, updateProduto, deleteProduto,
    addCategoria, updateCategoria, deleteCategoria,
  } = useStore();

  const [showModal, setShowModal] = useState(false);
  const [showCatModal, setShowCatModal] = useState(false);
  const [editing, setEditing] = useState<Produto | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [search, setSearch] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  const custoNum = Number(form.custo) || 0;
  const custoExtraNum = Number(form.custoExtra) || 0;
  const custoTotal = custoNum + custoExtraNum;

  let precoCalculado = 0;
  let margemCalculada = 0;

  if (form.modoPreco === 'margem') {
    precoCalculado = calcPrecoByMargem(custoNum, custoExtraNum, Number(form.margemDesejada) || 0);
    margemCalculada = Number(form.margemDesejada) || 0;
  } else if (form.modoPreco === 'lucro_fixo') {
    precoCalculado = calcPrecoByLucroFixo(custoNum, custoExtraNum, Number(form.lucroFixo) || 0);
    margemCalculada = calcMargem(custoNum, custoExtraNum, precoCalculado);
  } else {
    precoCalculado = Number(form.precoManual) || 0;
    margemCalculada = calcMargem(custoNum, custoExtraNum, precoCalculado);
  }

  const lucroUnitario = precoCalculado - custoTotal;

  const filtered = produtos.filter(p =>
    p.nome.toLowerCase().includes(search.toLowerCase()) ||
    p.categoria.toLowerCase().includes(search.toLowerCase())
  );

  // Top 10 categorias mais vendidas (por quantidade)
  const topCategorias = (() => {
    const map: Record<string, { qtd: number; receita: number }> = {};
    vendas.forEach(v => {
      const prod = produtos.find(p => p.id === v.produtoId);
      const cat = prod?.categoria ?? 'Sem categoria';
      if (!map[cat]) map[cat] = { qtd: 0, receita: 0 };
      map[cat].qtd += v.quantidade;
      map[cat].receita += v.valorLiquido;
    });
    return Object.entries(map)
      .sort((a, b) => b[1].qtd - a[1].qtd)
      .slice(0, 10)
      .map(([nome, stats], i) => ({ nome, ...stats, rank: i + 1 }));
  })();

  const maxQtd = topCategorias[0]?.qtd ?? 1;

  function openNew() {
    setEditing(null);
    const defaultCat = categorias[0]?.nome ?? '';
    setForm({ ...emptyForm, categoria: defaultCat });
    setShowModal(true);
  }

  function openEdit(p: Produto) {
    setEditing(p);
    setForm({
      nome: p.nome, categoria: p.categoria,
      custo: String(p.custo), custoExtra: String(p.custoExtra),
      estoque: String(p.estoque), status: p.status,
      modoPreco: 'manual', margemDesejada: String(p.margem.toFixed(0)),
      lucroFixo: '', precoManual: String(p.precoVenda),
    });
    setShowModal(true);
  }

  function handleSave() {
    if (!form.nome || !form.custo || precoCalculado <= 0 || !form.estoque) return;
    const data = {
      nome: form.nome, categoria: form.categoria,
      custo: custoNum, custoExtra: custoExtraNum,
      precoVenda: Number(precoCalculado.toFixed(2)),
      margem: Number(margemCalculada.toFixed(2)),
      estoque: Number(form.estoque), status: form.status,
    };
    if (editing) updateProduto(editing.id, data);
    else addProduto(data);
    setShowModal(false);
  }

  const modoLabels: Record<ModoPreco, string> = {
    margem: '% Margem',
    lucro_fixo: 'R$ Fixo',
    manual: 'Manual',
  };

  // Mini-dash calculations
  const totalCustoEstoque = produtos.reduce((acc, p) => acc + (p.custo + p.custoExtra) * p.estoque, 0);
  const totalVendaEstoque = produtos.reduce((acc, p) => acc + p.precoVenda * p.estoque, 0);
  const totalLucroPotencial = totalVendaEstoque - totalCustoEstoque;
  const totalUnidades = produtos.reduce((acc, p) => acc + p.estoque, 0);
  const margemMediaEstoque = totalVendaEstoque > 0 ? ((totalLucroPotencial / totalVendaEstoque) * 100) : 0;

  return (
    <AppLayout>
      <TopBar title="Produtos" subtitle="Gerencie seu catálogo" />
      <ActionBar>
        <div className="flex gap-2 w-full">
          <button
            onClick={() => setShowCatModal(true)}
            className="flex items-center justify-center gap-1.5 text-sm font-semibold py-2.5 px-4 rounded-xl cursor-pointer whitespace-nowrap bg-dark-card2 border border-dark-border text-dark-muted hover:text-dark-text hover:border-neon/30 transition-all"
          >
            <i className="ri-price-tag-3-line text-base"></i>
            <span className="hidden sm:inline">Categorias</span>
          </button>
          <button
            onClick={openNew}
            className="btn-gold flex-1 flex items-center justify-center gap-2 text-sm font-bold py-2.5 rounded-xl cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line text-base"></i> Novo Produto
          </button>
        </div>
      </ActionBar>

      <div className="pt-[196px] md:pt-[196px] p-3 md:p-6 space-y-3 animate-fade-in-up">

        {/* Mini-Dash Estoque */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-dark-card border border-dark-border rounded-xl p-4 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-20 h-20 bg-yellow-500/5 rounded-full -translate-y-6 translate-x-6"></div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-yellow-500/10">
                <i className="ri-money-dollar-circle-line text-yellow-400 text-sm"></i>
              </div>
              <span className="text-xs text-dark-muted font-medium">Capital em Estoque</span>
            </div>
            <p className="text-xl md:text-2xl font-bold font-mono text-yellow-400 leading-none mb-1">{fmt(totalCustoEstoque)}</p>
            <p className="text-[10px] text-dark-muted">{totalUnidades} unidades · custo total</p>
          </div>

          <div className="bg-dark-card border border-neon/20 rounded-xl p-4 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-20 h-20 bg-neon/5 rounded-full -translate-y-6 translate-x-6"></div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-neon/10">
                <i className="ri-line-chart-line text-neon text-sm"></i>
              </div>
              <span className="text-xs text-dark-muted font-medium">Se Vender Tudo</span>
            </div>
            <p className="text-xl md:text-2xl font-bold font-mono text-neon leading-none mb-1">{fmt(totalVendaEstoque)}</p>
            <p className="text-[10px] text-dark-muted">lucro potencial: <span className="text-green-400 font-semibold">{fmt(totalLucroPotencial)}</span></p>
          </div>
        </div>

        {/* Barra de lucro potencial */}
        {totalVendaEstoque > 0 && (
          <div className="bg-dark-card border border-dark-border rounded-xl px-4 py-3 flex items-center gap-4">
            <div className="flex-1">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[10px] text-dark-muted uppercase tracking-wide font-medium">Margem média do estoque</span>
                <span className={`text-xs font-bold font-mono ${margemMediaEstoque >= 50 ? 'text-neon' : margemMediaEstoque >= 30 ? 'text-yellow-400' : 'text-red-400'}`}>{margemMediaEstoque.toFixed(1)}%</span>
              </div>
              <div className="h-1.5 bg-dark-card2 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-700 ${margemMediaEstoque >= 50 ? 'bg-neon' : margemMediaEstoque >= 30 ? 'bg-yellow-400' : 'bg-red-400'}`}
                  style={{ width: `${Math.min(margemMediaEstoque, 100)}%` }}
                ></div>
              </div>
            </div>
            <div className="text-right shrink-0">
              <p className="text-[10px] text-dark-muted">Lucro potencial</p>
              <p className="text-sm font-bold font-mono text-green-400">{fmt(totalLucroPotencial)}</p>
            </div>
          </div>
        )}

        {/* Top Categorias */}
        {topCategorias.length > 0 && (
          <div className="bg-dark-card border border-dark-border rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-dark-border flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 flex items-center justify-center rounded-lg bg-neon/10">
                  <i className="ri-trophy-line text-neon text-xs"></i>
                </div>
                <span className="text-xs font-semibold text-dark-text">Top Categorias Mais Vendidas</span>
              </div>
              <span className="text-[10px] text-dark-muted">por quantidade</span>
            </div>
            <div className="p-3 space-y-2">
              {topCategorias.map((cat, i) => (
                <div key={cat.nome} className="flex items-center gap-3">
                  {/* Rank badge */}
                  <div className={`w-6 h-6 flex items-center justify-center rounded-full border text-[10px] font-bold shrink-0 ${i < 3 ? RANK_COLORS[i] : 'text-dark-muted bg-dark-card2 border-dark-border'}`}>
                    {cat.rank}
                  </div>
                  {/* Nome e barra */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-dark-text truncate">{cat.nome}</span>
                      <span className="text-[10px] text-dark-muted ml-2 shrink-0">{cat.qtd} und.</span>
                    </div>
                    <div className="h-1 bg-dark-card2 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-700 ${i === 0 ? 'bg-yellow-400' : i === 1 ? 'bg-slate-300' : i === 2 ? 'bg-amber-600' : 'bg-neon/50'}`}
                        style={{ width: `${(cat.qtd / maxQtd) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                  {/* Receita */}
                  <span className="text-xs font-mono font-semibold text-neon shrink-0">{fmt(cat.receita)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Search */}
        <div className="relative">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-dark-muted text-sm"></i>
          <input className="input-dark pl-9 text-sm" placeholder="Buscar produto..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>

        {/* Mobile: cards */}
        <div className="md:hidden space-y-2">
          {filtered.map(p => (
            <div key={p.id} className="bg-dark-card border border-dark-border rounded-xl p-4">
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-dark-text text-sm truncate">{p.nome}</p>
                  <p className="text-xs text-dark-muted">{p.categoria}</p>
                </div>
                <div className="flex items-center gap-1 ml-2 shrink-0">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${p.status === 'ativo' ? 'bg-neon/10 text-neon' : 'bg-dark-card2 text-dark-muted'}`}>{p.status}</span>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 mb-3">
                <div className="bg-dark-card2 rounded-lg p-2 text-center">
                  <p className="text-[9px] text-dark-muted uppercase mb-0.5">Custo</p>
                  <p className="text-xs font-bold font-mono text-dark-text">{fmt(p.custo + p.custoExtra)}</p>
                </div>
                <div className="bg-dark-card2 rounded-lg p-2 text-center">
                  <p className="text-[9px] text-dark-muted uppercase mb-0.5">Preço</p>
                  <p className="text-xs font-bold font-mono text-neon">{fmt(p.precoVenda)}</p>
                </div>
                <div className="bg-dark-card2 rounded-lg p-2 text-center">
                  <p className="text-[9px] text-dark-muted uppercase mb-0.5">Margem</p>
                  <p className="text-xs font-bold font-mono text-white">{p.margem.toFixed(1)}%</p>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className={`text-xs font-mono ${p.estoque === 0 ? 'text-red-400' : 'text-dark-muted'}`}>Estoque: {p.estoque}</span>
                <div className="flex gap-2">
                  <button onClick={() => openEdit(p)} className="w-8 h-8 flex items-center justify-center rounded-lg bg-dark-card2 text-dark-muted hover:text-dark-text cursor-pointer">
                    <i className="ri-edit-line text-sm"></i>
                  </button>
                  <button onClick={() => setConfirmDelete(p.id)} className="w-8 h-8 flex items-center justify-center rounded-lg bg-dark-card2 text-dark-muted hover:text-red-400 cursor-pointer">
                    <i className="ri-delete-bin-line text-sm"></i>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Desktop: table */}
        <div className="hidden md:block bg-dark-card border border-dark-border rounded-xl overflow-hidden">
          <div className="px-5 py-3 border-b border-dark-border">
            <span className="text-xs text-dark-muted">{filtered.length} produtos</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full table-dark">
              <thead>
                <tr>
                  <th className="text-left">Nome</th>
                  <th className="text-left">Categoria</th>
                  <th className="text-left">Custo Total</th>
                  <th className="text-left">Preço Venda</th>
                  <th className="text-left">Margem</th>
                  <th className="text-left">Estoque</th>
                  <th className="text-left">Status</th>
                  <th className="text-left">Ações</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(p => (
                  <tr key={p.id}>
                    <td className="font-medium text-dark-text">{p.nome}</td>
                    <td className="text-dark-muted">{p.categoria}</td>
                    <td className="font-mono text-sm">{fmt(p.custo + p.custoExtra)}</td>
                    <td className="font-mono text-sm text-neon font-bold">{fmt(p.precoVenda)}</td>
                    <td>
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${p.margem >= 50 ? 'bg-green-500/15 text-green-400' : 'bg-yellow-500/15 text-yellow-400'}`}>
                        {p.margem.toFixed(1)}%
                      </span>
                    </td>
                    <td className={`font-mono text-sm ${p.estoque === 0 ? 'text-red-400' : 'text-dark-text'}`}>{p.estoque}</td>
                    <td>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${p.status === 'ativo' ? 'bg-neon/10 text-neon' : 'bg-dark-card2 text-dark-muted'}`}>{p.status}</span>
                    </td>
                    <td>
                      <div className="flex items-center gap-2">
                        <button onClick={() => openEdit(p)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-dark-card2 text-dark-muted hover:text-dark-text cursor-pointer transition-colors"><i className="ri-edit-line text-sm"></i></button>
                        <button onClick={() => setConfirmDelete(p.id)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-500/10 text-dark-muted hover:text-red-400 cursor-pointer transition-colors"><i className="ri-delete-bin-line text-sm"></i></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modal Categorias */}
      {showCatModal && (
        <ModalCategorias
          categorias={categorias}
          onAdd={addCategoria}
          onUpdate={updateCategoria}
          onDelete={deleteCategoria}
          onClose={() => setShowCatModal(false)}
        />
      )}

      {/* Modal Produto */}
      {showModal && (
        <div className="fixed inset-0 z-50 modal-overlay flex items-center justify-center p-4">
          <div className="bg-dark-card border border-neon/15 rounded-2xl w-full max-w-sm p-5 animate-fade-in-up max-h-[88vh] overflow-y-auto shadow-[0_0_40px_rgba(57,255,20,0.07)]">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-bold text-dark-text">{editing ? 'Editar Produto' : 'Novo Produto'}</h2>
              <button onClick={() => setShowModal(false)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-dark-card2 text-dark-muted cursor-pointer"><i className="ri-close-line text-lg"></i></button>
            </div>

            <div className="space-y-3">
              {/* Nome */}
              <div>
                <label className="text-xs text-dark-muted mb-1 block font-medium">Nome do Produto</label>
                <input className="input-dark" placeholder="Ex: Camiseta Básica Preta" value={form.nome} onChange={e => setForm(f => ({ ...f, nome: e.target.value }))} />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-dark-muted mb-1 block font-medium">Categoria</label>
                  {categorias.length > 0 ? (
                    <select className="input-dark" value={form.categoria} onChange={e => setForm(f => ({ ...f, categoria: e.target.value }))}>
                      {categorias.map(c => <option key={c.id} value={c.nome}>{c.nome}</option>)}
                    </select>
                  ) : (
                    <div className="input-dark flex items-center gap-2 cursor-pointer" onClick={() => { setShowModal(false); setShowCatModal(true); }}>
                      <i className="ri-add-line text-dark-muted text-xs"></i>
                      <span className="text-xs text-dark-muted">Adicionar categoria</span>
                    </div>
                  )}
                </div>
                <div>
                  <label className="text-xs text-dark-muted mb-1 block font-medium">Status</label>
                  <select className="input-dark" value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value as 'ativo' | 'inativo' }))}>
                    <option value="ativo">Ativo</option>
                    <option value="inativo">Inativo</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-dark-muted mb-1 block font-medium">Custo (R$)</label>
                  <input type="number" className="input-dark" placeholder="0,00" value={form.custo} onChange={e => setForm(f => ({ ...f, custo: e.target.value }))} />
                </div>
                <div>
                  <label className="text-xs text-dark-muted mb-1 block font-medium">Custo Extra (R$)</label>
                  <input type="number" className="input-dark" placeholder="0,00" value={form.custoExtra} onChange={e => setForm(f => ({ ...f, custoExtra: e.target.value }))} />
                </div>
              </div>

              {/* Custo total preview */}
              {custoTotal > 0 && (
                <div className="bg-dark-card2 rounded-lg px-3 py-2 flex items-center justify-between">
                  <span className="text-xs text-dark-muted">Custo total</span>
                  <span className="text-sm font-bold font-mono text-dark-text">{fmt(custoTotal)}</span>
                </div>
              )}

              {/* Modo de precificação */}
              <div>
                <label className="text-xs text-dark-muted mb-2 block font-medium">Modo de Precificação</label>
                <div className="grid grid-cols-3 gap-1.5">
                  {(['margem', 'lucro_fixo', 'manual'] as ModoPreco[]).map(modo => (
                    <button
                      key={modo}
                      onClick={() => setForm(f => ({ ...f, modoPreco: modo }))}
                      className={`py-2 px-2 rounded-lg text-xs font-semibold cursor-pointer transition-all whitespace-nowrap
                        ${form.modoPreco === modo ? 'bg-neon text-black' : 'bg-dark-card2 text-dark-muted hover:text-dark-text'}`}
                    >
                      {modoLabels[modo]}
                    </button>
                  ))}
                </div>
              </div>

              {form.modoPreco === 'margem' && (
                <div>
                  <label className="text-xs text-dark-muted mb-1 block font-medium">Margem desejada (%)</label>
                  <div className="relative">
                    <input type="number" className="input-dark pr-8" placeholder="50" min="0" max="99" value={form.margemDesejada} onChange={e => setForm(f => ({ ...f, margemDesejada: e.target.value }))} />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-dark-muted text-sm font-bold">%</span>
                  </div>
                </div>
              )}

              {form.modoPreco === 'lucro_fixo' && (
                <div>
                  <label className="text-xs text-dark-muted mb-1 block font-medium">Lucro fixo desejado (R$)</label>
                  <input type="number" className="input-dark" placeholder="Ex: 50,00" value={form.lucroFixo} onChange={e => setForm(f => ({ ...f, lucroFixo: e.target.value }))} />
                </div>
              )}

              {form.modoPreco === 'manual' && (
                <div>
                  <label className="text-xs text-dark-muted mb-1 block font-medium">Preço de venda (R$)</label>
                  <input type="number" className="input-dark" placeholder="0,00" value={form.precoManual} onChange={e => setForm(f => ({ ...f, precoManual: e.target.value }))} />
                </div>
              )}

              {precoCalculado > 0 && (
                <div className="bg-neon/5 border border-neon/20 rounded-xl p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-dark-muted">Preço de venda</span>
                    <span className="text-lg font-bold font-mono text-neon">{fmt(precoCalculado)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-dark-muted">Lucro unitário</span>
                    <span className={`text-sm font-bold font-mono ${lucroUnitario > 0 ? 'text-green-400' : 'text-red-400'}`}>{fmt(lucroUnitario)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-dark-muted">Margem</span>
                    <span className={`text-sm font-bold font-mono ${margemCalculada >= 50 ? 'text-neon' : margemCalculada >= 30 ? 'text-yellow-400' : 'text-red-400'}`}>{margemCalculada.toFixed(1)}%</span>
                  </div>
                </div>
              )}

              <div>
                <label className="text-xs text-dark-muted mb-1 block font-medium">Estoque</label>
                <input type="number" className="input-dark" placeholder="0" value={form.estoque} onChange={e => setForm(f => ({ ...f, estoque: e.target.value }))} />
              </div>
            </div>

            <div className="flex gap-3 mt-5">
              <button onClick={() => setShowModal(false)} className="flex-1 py-3 rounded-xl border border-dark-border text-dark-muted text-sm font-semibold cursor-pointer whitespace-nowrap">Cancelar</button>
              <button onClick={handleSave} className="btn-gold flex-1 py-3 rounded-xl text-sm cursor-pointer whitespace-nowrap">{editing ? 'Salvar' : 'Criar Produto'}</button>
            </div>
          </div>
        </div>
      )}

      {confirmDelete && (
        <div className="fixed inset-0 z-50 modal-overlay flex items-center justify-center p-4">
          <div className="bg-dark-card border border-dark-border rounded-2xl w-full max-w-sm p-6 animate-fade-in-up">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-red-500/10 mx-auto mb-4"><i className="ri-delete-bin-line text-red-400 text-xl"></i></div>
            <h3 className="text-center text-base font-bold text-dark-text mb-2">Excluir produto?</h3>
            <p className="text-center text-sm text-dark-muted mb-5">Esta ação não pode ser desfeita.</p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmDelete(null)} className="flex-1 py-3 rounded-xl border border-dark-border text-dark-muted text-sm cursor-pointer whitespace-nowrap">Cancelar</button>
              <button onClick={() => { deleteProduto(confirmDelete); setConfirmDelete(null); }} className="flex-1 py-3 rounded-xl bg-red-500 text-white font-bold text-sm cursor-pointer whitespace-nowrap">Excluir</button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
